const STORAGE_KEY = 'csv_review_jobs';
const JOB_TTL_MS = 24 * 60 * 60 * 1000; // 24 hours

/**
 * Model for CSV HITL Review state
 * Unified single-step review: definitions + column details in one payload
 */
export class CsvReviewModel {
  constructor() {
    this.jobId = null;
    this.datasetId = null;
    this.phase = 'idle'; // idle | loading | reviewing | submitting | completed | failed
    this.reviewPayload = null;
    this.definitionSource = null; // document | llm_generated | partial_document
    this.suggestionsFailed = false; // true when LLM suggestions are unavailable (manual entry required)
    this.editedDefinitions = [];
    this.invalidColumns = []; // [{ table, csv_column }] flagged by a 422 incomplete_review response
    this.error = null;
    this.listeners = [];
  }

  // --- localStorage helpers (static, no instance needed) ---

  static persistJobMapping(datasetId, jobId, datasetName = '') {
    try {
      const mappings = JSON.parse(localStorage.getItem(STORAGE_KEY) || '{}');
      mappings[datasetId] = { jobId, name: datasetName, createdAt: Date.now() };
      localStorage.setItem(STORAGE_KEY, JSON.stringify(mappings));
    } catch (e) {
      console.error('[CsvReviewModel] Failed to persist job mapping:', e);
    }
  }

  static getPersistedJobId(datasetId) {
    try {
      const mappings = JSON.parse(localStorage.getItem(STORAGE_KEY) || '{}');
      const entry = mappings[datasetId];
      if (!entry) return null;
      const createdAt = typeof entry === 'string' ? 0 : (entry.createdAt || 0);
      if (!createdAt || Date.now() - createdAt > JOB_TTL_MS) {
        delete mappings[datasetId];
        localStorage.setItem(STORAGE_KEY, JSON.stringify(mappings));
        return null;
      }
      return entry.jobId;
    } catch (e) {
      console.error('[CsvReviewModel] Failed to read job mapping:', e);
      return null;
    }
  }

  static getPersistedMappings() {
    try {
      const mappings = JSON.parse(localStorage.getItem(STORAGE_KEY) || '{}');
      const now = Date.now();
      const active = [];
      let pruned = false;

      for (const [datasetId, entry] of Object.entries(mappings)) {
        const createdAt = typeof entry === 'string' ? 0 : (entry.createdAt || 0);
        if (!createdAt || now - createdAt > JOB_TTL_MS) {
          delete mappings[datasetId];
          pruned = true;
          continue;
        }
        active.push({
          datasetId,
          jobId: typeof entry === 'string' ? entry : entry.jobId,
          name: typeof entry === 'string' ? '' : (entry.name || ''),
        });
      }

      if (pruned) {
        localStorage.setItem(STORAGE_KEY, JSON.stringify(mappings));
      }
      return active;
    } catch (e) {
      console.error('[CsvReviewModel] Failed to read job mappings:', e);
      return [];
    }
  }

  static clearPersistedJob(datasetId) {
    try {
      const mappings = JSON.parse(localStorage.getItem(STORAGE_KEY) || '{}');
      delete mappings[datasetId];
      localStorage.setItem(STORAGE_KEY, JSON.stringify(mappings));
    } catch (e) {
      console.error('[CsvReviewModel] Failed to clear job mapping:', e);
    }
  }

  // --- Instance methods ---

  setJobId(jobId) {
    this.jobId = jobId;
    this.notify();
  }

  setDatasetId(datasetId) {
    this.datasetId = datasetId;
    this.notify();
  }

  setPhase(phase) {
    this.phase = phase;
    this.notify();
  }

  setError(error) {
    this.error = error;
    this.notify();
  }

  setReviewData(payload) {
    this.reviewPayload = payload;
    this.definitionSource = payload.definition_source || 'llm_generated';
    this.suggestionsFailed = !!payload.suggestions_failed;
    this.invalidColumns = [];

    // payload.tables[] is the authoritative list of columns to review. Render one
    // definition per table, covering every column (matched ∪ unmatched). When LLM
    // suggestions are unavailable, suggested_definitions may be empty — we must still
    // surface every table so the user can enter descriptions manually (PRD-893).
    const suggestedByName = {};
    (payload.suggested_definitions || []).forEach(d => { suggestedByName[d.snake_name] = d; });

    this.editedDefinitions = (payload.tables || []).map(table => {
      const suggested = suggestedByName[table.table_name];
      const hasSuggestion = !this.suggestionsFailed && !!suggested;
      // Prefer the per-definition source when present, else the payload-level source.
      const effectiveSource = (suggested && suggested.source) || this.definitionSource;
      const badge = hasSuggestion ? (effectiveSource === 'document' ? 'document' : 'ai') : null;

      // source_column is the immutable original CSV header each field maps to (PRD-949).
      // The backend sets it on each reviewable field; it round-trips on submit untouched
      // while the user is free to edit `name` (which becomes the DB column name). Prefer
      // an explicit source_column on the column, then the suggested definition's field,
      // and finally fall back to csv_column (the header itself for non-renamed fields).
      const suggestedSourceByKey = {};
      (suggested && suggested.fields ? suggested.fields : []).forEach(f => {
        if (f && f.source_column != null) {
          suggestedSourceByKey[f.source_column] = f.source_column;
          if (f.name != null) suggestedSourceByKey[f.name] = f.source_column;
        }
      });
      const resolveSource = (c) =>
        (c.source_column != null ? c.source_column : suggestedSourceByKey[c.csv_column]) ?? c.csv_column;

      // Field name MUST be the csv_column verbatim — that's what the submit contract
      // matches on. Matched columns carry a description; unmatched ones are prefilled
      // from suggested_description (empty under an LLM outage → user must type).
      // synthesized === true marks a field with no real CSV header — its name is an
      // AI-guessed label (or column_N fallback) and is the only one the user may edit
      // (PRD-949). Real headers (synthesized === false) are locked.
      const matched = (table.matched_columns || []).map(c => ({
        name: c.csv_column,
        description: c.description || '',
        pg_type: c.pg_type || 'TEXT',
        source_column: resolveSource(c),
        synthesized: !!c.synthesized,
        isAiSuggested: false,
      }));
      const unmatched = (table.unmatched_columns || []).map(c => ({
        name: c.csv_column,
        description: c.suggested_description || c.description || '',
        pg_type: c.suggested_pg_type || c.pg_type || 'TEXT',
        source_column: resolveSource(c),
        synthesized: !!c.synthesized,
        isAiSuggested: true,
      }));

      return {
        name: (suggested && suggested.name) || table.table_name,
        snake_name: table.table_name,
        description: (suggested && suggested.description) || '',
        source: effectiveSource,
        // 'ai' | 'document' | null — drives the def-card badge; suppressed on outage
        badge,
        isAiSuggested: badge === 'ai',
        fields: [...matched, ...unmatched],
      };
    });

    this.notify();
  }

  setInvalidColumns(columns) {
    this.invalidColumns = Array.isArray(columns) ? columns : [];
    this.notify();
  }

  updateDefinition(defIndex, field, value) {
    if (this.editedDefinitions[defIndex]) {
      this.editedDefinitions[defIndex][field] = value;
      this.notify();
    }
  }

  updateDefinitionField(defIndex, fieldIndex, fieldKey, value) {
    const def = this.editedDefinitions[defIndex];
    if (def && def.fields[fieldIndex]) {
      def.fields[fieldIndex][fieldKey] = value;
      // Clear a 422 highlight once the user fills the offending description.
      if (fieldKey === 'description' && value && value.trim() && this.invalidColumns.length) {
        const csvColumn = def.fields[fieldIndex].name;
        this.invalidColumns = this.invalidColumns.filter(
          c => !(c.table === def.snake_name && c.csv_column === csvColumn)
        );
      }
      this.notify();
    }
  }

  addField(defIndex) {
    const def = this.editedDefinitions[defIndex];
    if (def) {
      def.fields.push({ name: '', description: '', isAiSuggested: false });
      this.notify();
    }
  }

  removeField(defIndex, fieldIndex) {
    const def = this.editedDefinitions[defIndex];
    if (def && def.fields.length > 1) {
      def.fields.splice(fieldIndex, 1);
      this.notify();
    }
  }

  reset() {
    this.jobId = null;
    this.datasetId = null;
    this.phase = 'idle';
    this.reviewPayload = null;
    this.definitionSource = null;
    this.suggestionsFailed = false;
    this.editedDefinitions = [];
    this.invalidColumns = [];
    this.error = null;
    this.notify();
  }

  subscribe(listener) {
    this.listeners.push(listener);
    return () => {
      this.listeners = this.listeners.filter(l => l !== listener);
    };
  }

  notify() {
    const state = this.getState();
    this.listeners.forEach(listener => listener(state));
  }

  getState() {
    return {
      jobId: this.jobId,
      datasetId: this.datasetId,
      phase: this.phase,
      reviewPayload: this.reviewPayload,
      definitionSource: this.definitionSource,
      suggestionsFailed: this.suggestionsFailed,
      editedDefinitions: this.editedDefinitions,
      invalidColumns: this.invalidColumns,
      error: this.error,
    };
  }
}
